import { addDoc, collection, deleteDoc, deleteField, doc, getDocs, query, Timestamp, updateDoc, where, writeBatch } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { DEFAULT_TZ, createDateInZone } from '../../../shared/utils/timezone';
import { Squad } from '../../../types';
import { toZonedTime } from 'date-fns-tz';
import type { PartnerCalendarEntry } from '../../partners/types';
import {
  inheritedNoClassDatesForLocation,
  sameDateList,
  squadInheritsNoClassDays,
} from '../../partners/utils/partnerCalendarEntries';

// Helper: format Date to YYYY-MM-DD in given timezone
const toDateStrInZone = (date: Date, tz: string) => {
  const zoned = toZonedTime(date, tz);
  const y = zoned.getFullYear();
  const m = String(zoned.getMonth() + 1).padStart(2, '0');
  const d = String(zoned.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const dayCodeToIndex: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

export interface SyncOptions {
  // limit to today forward relative to squad timezone
  futureOnly?: boolean;
}

/**
 * Generate the set of required YYYY-MM-DD strings for the squad's schedule.
 */
export function buildRequiredDateSet(squad: Partial<Squad>): Set<string> {
  const tz = squad.timezone || DEFAULT_TZ;
  const startDate = squad.schedule?.startDate;
  const endDate = squad.schedule?.endDate;
  const dayOfWeek = squad.schedule?.dayOfWeek as keyof typeof dayCodeToIndex | undefined;
  // No-class days combine the squad's own exceptions with partner-wide days
  // materialized onto the squad by refreshInheritedNoClassDays.
  const exceptions = new Set([
    ...(squad.schedule?.exceptions || []),
    ...(squad.schedule?.inheritedExceptions || []),
  ].filter(Boolean));
  const makeups = (squad.schedule?.makeups || []).filter(Boolean);
  const required = new Set<string>();
  if (squad.scheduleKind === 'single_day' || squad.schedule?.mainDate) {
    if (squad.schedule?.mainDate) required.add(squad.schedule.mainDate);
    return required;
  }
  if (!startDate || !endDate || !dayOfWeek) return required;

  const targetDow = dayCodeToIndex[dayOfWeek];

  // Helper: advance a YYYY-MM-DD string by one calendar day safely (DST-agnostic)
  const nextYmd = (ymd: string): string => {
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + 1);
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(dt.getUTCDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };

  // Iterate inclusively from startDate to endDate as local days in tz
  let ymd = startDate;
  while (ymd <= endDate) {
    // Build the local midnight instant and determine weekday in tz
    const localMidnight = createDateInZone(ymd, '00:00', tz);
    const zoned = toZonedTime(localMidnight, tz);
    const dowInTz = zoned.getUTCDay(); // read components in the target zone

    if (dowInTz === targetDow) {
      if (!exceptions.has(ymd)) required.add(ymd);
    }

    ymd = nextYmd(ymd);
  }

  // Add makeups explicitly
  for (const d of makeups) required.add(d);

  // Do not trim past dates: preserve historical events and only prune when schedule truly excludes a date

  return required;
}

/** Sorted YYYY-MM-DD teaching days from schedule (DOW + range − exceptions + makeups). */
export function listSquadTeachingDates(squad: Partial<Squad>): string[] {
  return Array.from(buildRequiredDateSet(squad)).sort();
}

/**
 * Recompute the partner-wide no-class days this squad inherits and persist them
 * on the squad doc (schedule.inheritedExceptions). Returns the squad with the
 * fresh list applied so callers can compute dates from it immediately.
 *
 * Best-effort: if the partner calendar cannot be read (permissions, offline),
 * the stored list is kept as-is.
 */
export async function refreshInheritedNoClassDays<T extends { id: string } & Partial<Squad>>(squad: T): Promise<T> {
  const partnerId = String(squad.partnerId || '').trim();
  if (!partnerId || !squadInheritsNoClassDays(squad)) return squad;
  try {
    const snap = await getDocs(collection(db, 'partners', partnerId, 'calendarEntries'));
    const entries = snap.docs.map((item) => ({ id: item.id, ...(item.data() as any) })) as PartnerCalendarEntry[];
    const inherited = inheritedNoClassDatesForLocation(entries, squad.locationId);
    if (!sameDateList(squad.schedule?.inheritedExceptions, inherited)) {
      await updateDoc(doc(db, 'squads', squad.id), { 'schedule.inheritedExceptions': inherited } as any);
    }
    return { ...squad, schedule: { ...(squad.schedule || {}), inheritedExceptions: inherited } };
  } catch (error) {
    console.warn('Could not refresh inherited no-class days; using stored values.', error);
    return squad;
  }
}

export async function syncSquadCalendarFromDetails(squadInput: { id: string } & Partial<Squad>, options: SyncOptions = { futureOnly: true }) {
  const squad = await refreshInheritedNoClassDays(squadInput);
  const tz = squad.timezone || DEFAULT_TZ;
  const titleFallback = squad.title || 'Class';
  const startTime = squad.schedule?.startTime || '15:30';
  const endTime = squad.schedule?.endTime || '16:30';
  const address = String((squad as any)?.address || (squad as any)?.location?.address || '').trim() || null;
  const schoolName = String((squad as any)?.schoolName || '').trim() || null;
  const locationName = String((squad as any)?.locationName || (squad as any)?.location?.name || '').trim() || null;

  const coTeacherIds = Array.from(new Set(
    (squad.coTeachers || [])
      .map(c => String(c?.teacherId || '').trim())
      .filter(Boolean)
  ));

  const requiredDates = buildRequiredDateSet(squad);
  const todayYmd = toDateStrInZone(new Date(), tz);

  // Fetch existing events for this squad
  const eventsRef = collection(db, 'squadEvents');
  const q = query(eventsRef, where('squadId', '==', squad.id));
  const snap = await getDocs(q);

  // Cancellation retains the squad record but removes every calendar occurrence.
  // Keep this guard in the shared sync path so editing a canceled squad cannot
  // accidentally recreate events while the server-side cleanup is still running.
  if (['canceled', 'cancelled'].includes(String(squad.status || '').trim().toLowerCase())) {
    for (let index = 0; index < snap.docs.length; index += 450) {
      const cancelBatch = writeBatch(db);
      snap.docs.slice(index, index + 450).forEach((eventDoc) => cancelBatch.delete(eventDoc.ref));
      await cancelBatch.commit();
    }
    return;
  }

  type Existing = {
    id: string;
    dateKey: string; // YYYY-MM-DD in squad tz
    start: Date;
    end: Date;
    mainTeacherId?: string;
    title?: string;
    syncedFromSquadDetails?: boolean;
  };

  const existingSyncedByDate = new Map<string, Existing>();
  const existingUnflagged: Existing[] = [];

  snap.forEach(d => {
    const data: any = d.data();
    const start: Date = data.start.toDate();
    const end: Date = data.end.toDate();
    const dateKey = toDateStrInZone(start, tz);
    const item: Existing = {
      id: d.id,
      dateKey,
      start,
      end,
      mainTeacherId: data.mainTeacherId,
      title: data.title,
      syncedFromSquadDetails: !!data.syncedFromSquadDetails,
    };
    if (item.syncedFromSquadDetails) {
      existingSyncedByDate.set(dateKey, item);
    } else {
      existingUnflagged.push(item); // manual events, leave untouched
    }
  });

  const batch = writeBatch(db);

  // Prepare a quick lookup for the first unflagged event per dateKey (to adopt)
  const existingUnflaggedByDate = new Map<string, Existing>();
  for (const evt of existingUnflagged) {
    if (!existingUnflaggedByDate.has(evt.dateKey)) {
      existingUnflaggedByDate.set(evt.dateKey, evt);
    }
  }

  // Deletes for dates no longer required (only previously-synced)
  for (const [dateKey, evt] of existingSyncedByDate.entries()) {
    // Do not delete historical events (before today in squad tz)
    if (!requiredDates.has(dateKey) && dateKey >= todayYmd) {
      batch.delete(doc(db, 'squadEvents', evt.id));
    }
  }

  // Adds/updates required dates
  for (const dateKey of requiredDates) {
    const existing = existingSyncedByDate.get(dateKey);
    const start = createDateInZone(dateKey, startTime, tz);
    const end = createDateInZone(dateKey, endTime, tz);

    if (existing) {
      // Update time + title + location metadata, preserve teacher assignments
      const evtRef = doc(db, 'squadEvents', existing.id);
      batch.update(evtRef, {
        title: titleFallback,
        start,
        end,
        address,
        schoolName,
        locationName,
      } as any);
    } else {
      // Try to adopt an existing unflagged event for this dateKey
      const adopt = existingUnflaggedByDate.get(dateKey);
      if (adopt) {
        const evtRef = doc(db, 'squadEvents', adopt.id);
        // Update timing + title + location metadata and mark as managed by sync going forward
        batch.update(evtRef, {
          title: titleFallback,
          start,
          end,
          syncedFromSquadDetails: true,
          address,
          schoolName,
          locationName,
        });
      } else {
        // Add new event if none to adopt
        const payload: any = {
          title: titleFallback,
          squadId: squad.id,
          start,
          end,
          supportingTeacherIds: coTeacherIds,
          syncedFromSquadDetails: true,
          address,
          schoolName,
          locationName,
          // By default, these events follow the squad-level lead teacher unless explicitly overridden later
          mainTeacherOverridden: false,
          createdAt: Timestamp.now(),
        };
        if (squad.regularTeacher) {
          payload.mainTeacherId = String(squad.regularTeacher);
        }
        batch.set(doc(collection(db, 'squadEvents')), payload);
      }
    }
  }

  await batch.commit();
}

/**
 * When a squad's co-teacher list changes, update future auto-scheduled events'
 * supportingTeacherIds: add newly added co-teachers and drop removed ones,
 * while preserving any supporting teachers added manually on individual events.
 *
 * Rules mirror syncSquadTeachersFromLeadChange:
 * - Only events for this squad today-forward (in squad tz)
 * - Only events managed by squad details (syncedFromSquadDetails === true)
 */
export async function syncSquadCoTeachersChange(params: {
  squadId: string;
  timezone?: string;
  oldCoTeacherIds: string[];
  newCoTeacherIds: string[];
}) {
  const { squadId, timezone, oldCoTeacherIds, newCoTeacherIds } = params;
  const tz = timezone || DEFAULT_TZ;
  const todayYmd = toDateStrInZone(new Date(), tz);

  const oldSet = new Set(oldCoTeacherIds.map(String).filter(Boolean));
  const newSet = new Set(newCoTeacherIds.map(String).filter(Boolean));
  const added = Array.from(newSet).filter(id => !oldSet.has(id));
  const removed = new Set(Array.from(oldSet).filter(id => !newSet.has(id)));
  if (added.length === 0 && removed.size === 0) return;

  const eventsRef = collection(db, 'squadEvents');
  const q = query(eventsRef, where('squadId', '==', squadId));
  const snap = await getDocs(q);

  const batch = writeBatch(db);
  let updated = 0;
  let coveredSkipped = 0;

  snap.forEach(d => {
    const data: any = d.data();
    const current: string[] = Array.isArray(data.supportingTeacherIds)
      ? data.supportingTeacherIds.map((id: any) => String(id || '')).filter(Boolean)
      : [];
    const nextAfterRemoval = current.filter(id => !removed.has(id));

    // Removal is an access/safety operation, not a schedule-default change. A
    // removed co-teacher must disappear from every occurrence, including past,
    // manually-created, and covered events. Additions remain scoped below.
    if (nextAfterRemoval.length !== current.length) {
      batch.update(doc(db, 'squadEvents', d.id), { supportingTeacherIds: nextAfterRemoval });
      updated += 1;
      return;
    }

    if (!data.start || !data.syncedFromSquadDetails) return;
    // Coverage snapshots intentionally freeze the underlying assignment for this
    // occurrence until the request is canceled or completed. Note this SKIPS the
    // roster change for that occurrence permanently (no deferred reconciliation);
    // the counter below keeps that visible for operations.
    if (Array.isArray(data.activeCoverage) && data.activeCoverage.length > 0) {
      coveredSkipped += 1;
      return;
    }
    const dateKey = toDateStrInZone(data.start.toDate(), tz);
    if (dateKey < todayYmd) return;

    const next = [...current];
    for (const id of added) {
      if (!next.includes(id)) next.push(id);
    }
    if (next.length === current.length && next.every((id, i) => id === current[i])) return;

    batch.update(doc(db, 'squadEvents', d.id), { supportingTeacherIds: next });
    updated += 1;
  });

  if (updated > 0) await batch.commit();
  if (coveredSkipped > 0) {
    console.warn(
      `[calendarSync] Co-teacher roster change for squad ${squadId} skipped ${coveredSkipped} event(s) with active substitute coverage; re-apply after the coverage resolves if the change should reach those dates.`,
    );
  }
}

/** Remove one teacher from every staffing/access reference on this squad's events. */
export async function removeTeacherFromSquadEvents(params: {
  squadId: string;
  teacherId: string;
}) {
  const squadId = String(params.squadId || '').trim();
  const teacherId = String(params.teacherId || '').trim();
  if (!squadId || !teacherId) return;

  const snap = await getDocs(query(collection(db, 'squadEvents'), where('squadId', '==', squadId)));
  const batch = writeBatch(db);
  let updated = 0;

  snap.forEach(eventDoc => {
    const data: any = eventDoc.data();
    const patch: Record<string, any> = {};
    if (String(data.mainTeacherId || '') === teacherId) {
      patch.mainTeacherId = deleteField();
      patch.mainTeacherOverridden = false;
    }
    const supporting = Array.isArray(data.supportingTeacherIds) ? data.supportingTeacherIds.map(String) : [];
    if (supporting.includes(teacherId)) patch.supportingTeacherIds = supporting.filter((id: string) => id !== teacherId);
    const participants = Array.isArray(data.eventParticipantIds) ? data.eventParticipantIds.map(String) : [];
    if (participants.includes(teacherId)) patch.eventParticipantIds = participants.filter((id: string) => id !== teacherId);
    if (String(data.subAssignedTeacherId || '') === teacherId) patch.subAssignedTeacherId = deleteField();
    if (String(data.subRequestedByTeacherId || '') === teacherId) patch.subRequestedByTeacherId = deleteField();
    if (String(data.subOriginalMainTeacherId || '') === teacherId) patch.subOriginalMainTeacherId = deleteField();
    const coverage = Array.isArray(data.activeCoverage) ? data.activeCoverage : [];
    const remainingCoverage = coverage.filter((entry: any) =>
      String(entry?.requestedTeacherId || '') !== teacherId
      && String(entry?.substituteTeacherId || '') !== teacherId
    );
    if (remainingCoverage.length !== coverage.length) patch.activeCoverage = remainingCoverage;

    if (Object.keys(patch).length > 0) {
      batch.update(doc(db, 'squadEvents', eventDoc.id), patch);
      updated += 1;
    }
  });

  if (updated > 0) await batch.commit();
}

/**
 * When a squad's lead teacher (regularTeacher) changes, update future
 * auto-scheduled events that are still following the old default teacher.
 * When the lead is removed without a replacement, remove that teacher from the
 * main-teacher slot on every squad event where they are still assigned.
 *
 * Rules:
 * - Only touch events for this squad in the future (>= today in squad tz)
 * - Only touch events created/managed by squad details (syncedFromSquadDetails === true)
 * - Never touch events explicitly marked as overrides (mainTeacherOverridden === true)
 * - For remaining events:
 *    - If mainTeacherId equals oldRegularTeacher, or is null/undefined when oldRegularTeacher is null,
 *      set mainTeacherId to newRegularTeacher
 */
export async function syncSquadTeachersFromLeadChange(params: {
  squadId: string;
  timezone?: string;
  oldRegularTeacher: string | null;
  newRegularTeacher: string | null;
  overwriteAllEvents?: boolean;
}) {
  const { squadId, timezone, oldRegularTeacher, newRegularTeacher, overwriteAllEvents = false } = params;
  const tz = timezone || DEFAULT_TZ;
  const todayYmd = toDateStrInZone(new Date(), tz);

  console.log('[TeacherSync] Starting syncSquadTeachersFromLeadChange', {
    squadId,
    timezone: tz,
    oldRegularTeacher,
    newRegularTeacher,
    todayYmd,
  });

  const eventsRef = collection(db, 'squadEvents');
  const q = query(eventsRef, where('squadId', '==', squadId));
  const snap = await getDocs(q);

  const batch = writeBatch(db);

  let total = 0;
  let future = 0;
  let managed = 0;
  let overridden = 0;
  let covered = 0;
  let updated = 0;
  let skippedWrongTeacher = 0;

  snap.forEach(d => {
    total += 1;
    const data: any = d.data();
    if (!data.start) return;

    const currentMain: string | null = data.mainTeacherId || null;

    // Removing a squad lead is deliberately broader than replacing one. The
    // teacher must no longer appear as the lead on any occurrence, including
    // past, manually overridden, and currently covered occurrences. Coverage
    // remains intact because it is stored separately in activeCoverage.
    if (!newRegularTeacher) {
      if (!oldRegularTeacher || currentMain !== oldRegularTeacher) return;
      const evtRef = doc(db, 'squadEvents', d.id);
      batch.update(evtRef, {
        mainTeacherId: deleteField(),
        mainTeacherOverridden: false,
      });
      updated += 1;
      return;
    }

    // Explicit admin choice: make the squad lead authoritative for every
    // occurrence. Coverage remains stored separately and continues to display
    // the substitute until that coverage is resolved.
    if (overwriteAllEvents) {
      const evtRef = doc(db, 'squadEvents', d.id);
      batch.update(evtRef, {
        mainTeacherId: newRegularTeacher,
        mainTeacherOverridden: false,
      });
      updated += 1;
      return;
    }

    const start: Date = data.start.toDate();
    const dateKey = toDateStrInZone(start, tz);

    // Only consider today-forward
    if (dateKey < todayYmd) {
      return;
    }
    future += 1;

    // Only modify events that are managed by squad details
    if (!data.syncedFromSquadDetails) {
      return;
    }
    managed += 1;

    if (Array.isArray(data.activeCoverage) && data.activeCoverage.length > 0) {
      covered += 1;
      return;
    }

    // Respect explicit overrides
    if (data.mainTeacherOverridden === true) {
      overridden += 1;
      return;
    }

    // If there was a previous regular teacher, only update events that still reference it
    if (oldRegularTeacher) {
      if (currentMain && currentMain !== oldRegularTeacher) {
        // This event has been pointed at a different teacher; treat as manual
        skippedWrongTeacher += 1;
        return;
      }
    } else {
      // No prior regular teacher; only update events with no main teacher set
      if (currentMain) {
        skippedWrongTeacher += 1;
        return;
      }
    }

    const evtRef = doc(db, 'squadEvents', d.id);
    batch.update(evtRef, {
      mainTeacherId: newRegularTeacher,
    });
    updated += 1;
  });

  await batch.commit();

  console.log('[TeacherSync] Completed syncSquadTeachersFromLeadChange', {
    squadId,
    totalEvents: total,
    futureEvents: future,
    managedByDetails: managed,
    explicitlyOverridden: overridden,
    activeCoverageSkipped: covered,
    updatedEvents: updated,
    skippedWrongTeacher,
  });
}
